/**
 * Copy-to-clipboard for share links.
 *
 * Finds buttons with `data-copy-url`, writes the URL to the clipboard on
 * click, and shows a short "Link copied!" bubble anchored above the
 * button. The bubble has role="status" + aria-live="polite" so screen
 * readers announce it without interrupting the user.
 */

const TOAST_CLASS = "copy-link-toast";
const TOAST_VISIBLE = "is-visible";
const TOAST_LEAVING = "is-leaving";
const HIDE_MS = 2800;
const FADE_MS = 120;

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for insecure contexts or legacy browsers.
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("execCommand copy failed");
}

function showToast(btn, text, isError = false) {
  // Remove any existing toast attached to this button so repeat clicks
  // re-trigger the announcement.
  btn.querySelectorAll("." + TOAST_CLASS).forEach((el) => el.remove());

  const toast = document.createElement("span");
  toast.className = TOAST_CLASS + (isError ? " is-error" : "");
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.textContent = text;
  btn.appendChild(toast);

  // Let the element mount before animating so the transition runs.
  requestAnimationFrame(() => toast.classList.add(TOAST_VISIBLE));

  setTimeout(() => {
    // Swap to the "leaving" state so it drifts upward on its way out
    // rather than dropping back into the button.
    toast.classList.remove(TOAST_VISIBLE);
    toast.classList.add(TOAST_LEAVING);
    setTimeout(() => toast.remove(), FADE_MS);
  }, HIDE_MS);
}

async function handleCopyClick(e) {
  const btn = e.target.closest("[data-copy-url]");
  if (!btn) return;
  e.preventDefault();

  const url = btn.dataset.copyUrl || window.location.href;

  try {
    await copyText(url);
    showToast(btn, "Link copied!");
  } catch (err) {
    showToast(btn, "Copy failed", true);
  }
}

document.addEventListener("click", handleCopyClick);
